/** Общие части обвязки читалки: оглавление, настройки вида, подсказка о
 *  прочитанном на другом устройстве. Одинаковы в обеих читалках проекта. */

import type { Manifest } from '../api'
import { DEFAULT_SETTINGS, type ReaderSettings, type Theme } from './settings'
import type { Reader } from './useReader'

const THEME_LABELS: Record<Theme, string> = {
  dark: 'Тёмная',
  sepia: 'Сепия',
  light: 'Светлая',
}

export function TocPanel({
  manifest,
  current,
  onPick,
}: {
  manifest: Manifest
  current: number
  onPick: (index: number) => void
}) {
  return (
    <div className="panel">
      <h3>Оглавление</h3>
      {manifest.chapters.map((item) => (
        <button
          key={item.index}
          className={`toc__item${item.index === current ? ' toc__item--active' : ''}`}
          onClick={() => onPick(item.index)}
        >
          {item.title || `Глава ${item.index + 1}`}
        </button>
      ))}
    </div>
  )
}

export function SettingsPanel({
  settings,
  onChange,
  onClose,
  extra,
}: {
  settings: ReaderSettings
  onChange: (patch: Partial<ReaderSettings>) => void
  onClose: () => void
  /** Дополнительные переключатели: языковая читалка добавляет свои. */
  extra?: React.ReactNode
}) {
  return (
    <div className="panel">
      <h3>Вид</h3>
      <div className="panel__row">
        <span>Тема</span>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          {(['dark', 'sepia', 'light'] as Theme[]).map((theme) => (
            <button
              key={theme}
              className={`btn${settings.theme === theme ? ' btn--primary' : ''}`}
              onClick={() => onChange({ theme })}
            >
              {THEME_LABELS[theme]}
            </button>
          ))}
        </div>
      </div>
      <Slider
        label="Размер шрифта"
        value={settings.fontSize}
        min={14}
        max={30}
        step={1}
        suffix="px"
        onChange={(fontSize) => onChange({ fontSize })}
      />
      <Slider
        label="Межстрочный интервал"
        value={settings.lineHeight}
        min={1.2}
        max={2.2}
        step={0.05}
        onChange={(lineHeight) => onChange({ lineHeight })}
      />
      <Slider
        label="Ширина колонки"
        value={settings.width}
        min={26}
        max={60}
        step={1}
        suffix="rem"
        onChange={(width) => onChange({ width })}
      />
      {extra}
      <div className="panel__row">
        <button className="btn" onClick={() => onChange(DEFAULT_SETTINGS)}>
          Сбросить
        </button>
        <button className="btn" onClick={onClose}>
          Готово
        </button>
      </div>
    </div>
  )
}

/** Предложение перейти туда, где прочитано дальше на другом устройстве. */
export function ElsewhereToast({ reader }: { reader: Reader }) {
  const { elsewhere, goToChapter, dismissElsewhere } = reader
  if (!elsewhere) return null
  return (
    <div className="toast">
      <span>На другом устройстве прочитано до {Math.round(elsewhere.max_progress * 100)} %.</span>
      <button
        className="btn btn--primary"
        onClick={() => {
          goToChapter(elsewhere.max_chapter ?? 0, elsewhere.max_block ?? 0)
          dismissElsewhere()
        }}
      >
        Перейти
      </button>
      <button className="btn btn--ghost" onClick={dismissElsewhere}>
        Остаться
      </button>
    </div>
  )
}

export function ChapterNav({
  index,
  isLast,
  onGo,
}: {
  index: number
  isLast: boolean
  onGo: (index: number) => void
}) {
  return (
    <div className="reader__nav">
      <button className="btn" disabled={index === 0} onClick={() => onGo(index - 1)}>
        ← Предыдущая
      </button>
      <button className="btn" disabled={isLast} onClick={() => onGo(index + 1)}>
        Следующая →
      </button>
    </div>
  )
}

export function Slider({
  label,
  value,
  min,
  max,
  step,
  suffix = '',
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  suffix?: string
  onChange: (value: number) => void
}) {
  return (
    <div style={{ padding: '0.5rem 0' }}>
      <div className="panel__row" style={{ padding: 0 }}>
        <span>{label}</span>
        <span className="dim">
          {value}
          {suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ width: '100%' }}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </div>
  )
}
